import React, { createContext, PropsWithChildren, useContext, useEffect, useRef, useState } from 'react';
import { NavigateOptions, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useCookies, withCookies } from 'react-cookie';
import { getAuth, signInWithCustomToken, User, UserCredential } from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { v4 as uuidv4 } from 'uuid';

import { getCurrentRequestId } from '../lib/performance';
import { getNormalizedPhotoUrl } from '../lib/images';
import { createElkUser, getElkUser, getEventImageHistory, getHasCompletedElkProfile } from '../api/ElkEventService';
import { app, auth } from '../core/libs/Firebase';
import { loginUrl } from '../lib/login';

import useLocalStorage from 'Common/src/hooks/useLocalStorage';
import { logSumoEvent, ULogApplication, ULogSeverity, ULogTag } from 'Common/src/api/SumoLogicApi';
import { promiseTimeout, TimeoutError } from 'Common/src/utils/PromiseTimeout';
import { authenticate, refetchAccessToken, sendPhoneSignInCode, signInWithPhoneAndCode } from 'Common/src/api/AuthenticationApi';

import { TAppProduct, TAuthHeader, TUserAgent } from 'TProtocol/common/models';
import {
  TElkGetImageHistoryResponse,
  TAppElkTextPermissionStatus,
  TAppElkUser,
  TElkGetUserResponse
} from 'TProtocol/prototypes/events/messages';
import { TSendPhoneSignInCodeResponse } from 'TProtocol/authentication/messages';

export interface IUUserContext {
  login: () => void;
  loginState: ULoginState;
  id?: string;
  token?: string;
  photoUrl?: string;
  getEmail: () => string | undefined;
  emails?: string[];
  hasNudgedToAllowTexts: boolean;
  accountExistsForPhone?: boolean;
  askForTextPermission: boolean;
  setAskForTextPermission: (askForTextPermission: boolean) => void;
  grantedTextPermission?: boolean;
  checkForAccount: (phoneNumber: string) => Promise<void>;
  isLoggedIn: () => boolean;
  confirmCode: (code: string) => Promise<void>;
  restartLogin: () => void;
  handleLogin: (firebaseUser: User, goTo?: string) => Promise<void>;
  continueLogin: () => Promise<void>;
  finishLogin: (goTo?: string, options?: NavigateOptions) => void;
  submitPhone: (simplePhone: string, force?: boolean) => Promise<void>;
  getAuthHeader: (isBlocking?: boolean) => TAuthHeader;
  getPhoneNumber: () => Promise<string | null | undefined>;
  logout: () => Promise<void>;
  getUserName: () => Promise<string | null | undefined>;
  setPhoneNumber: (phone: string) => void;
  setName: (name: string) => void;
  setPhotoUrl: (photoUrl?: string) => void;
  name: string;
  phoneNumber: string;
  createUser: (name: string, grantedTextPermissions: boolean, coinsSpent: number) => Promise<void>;
  generateElkUser: (partialUser?: Partial<TAppElkUser>) => TAppElkUser | undefined;
  setHasNudgedToAllowTexts: (nudged: boolean) => void;
  setGrantedTextPermissions: (permissionGranted: boolean) => void;
  setEmail: (emailAddress: string) => void;
  deviceId: string;
  fetchEventImageHistory: () => Promise<TElkGetImageHistoryResponse>;
}

interface UUser {
  id: string;
  authToken: string;
  name: string;
  phoneNumber: string;
  photoUrl?: string;
  grantedTextPermission?: boolean;
  emails?: string[];
}

export enum ULoginStatus {
  NotLoggedIn = 'NOT_LOGGED_IN',
  PhoneSubmitting = 'PHONE_SUBMITTING',
  CodeSent = 'CODE_SENT',
  CodeSubmitting = 'CODE_SUBMITTING',
  SignedInToFirebase = 'SIGNED_IN_TO_FIREBASE',
  WaitingForAccountCheck = 'WAITING_FOR_ACCOUNT_CHECK',
  AfterLogin = 'AFTER_LOGIN',
  AskingForName = 'ASKING_FOR_NAME',
  NameSubmitting = 'NAME_SUBMITTING',
  UserCreated = 'USER_CREATED',
  LoggedIn = 'LOGGED_IN',
  ErrorDuringLogin = 'ERROR_DURING_LOGIN'
}

export interface ULoginState {
  phone?: string;
  status: ULoginStatus;
  signInResponse?: TSendPhoneSignInCodeResponse;
  codeResendTimestamp?: number;
  error?: string;
}

export interface UUserInputs {
  name: string;
  grantedTextPermissions: boolean;
  coinsSpent: number;
}

const sixtyDays = 60 * 60 * 24 * 60;

const UserContext = createContext<IUUserContext | null>(null);

export const UserContextProvider = withCookies((props: PropsWithChildren<object>) => {
  const [cookies, setCookie, deleteCookie] = useCookies(
    ['userId', 'authToken']
  );
  const [deviceId, setDeviceId] = useLocalStorage<string>('deviceId', '');
  const navigate = useNavigate();
  const location = useLocation();
  const [queryParams] = useSearchParams();

  const queryUserId = queryParams.get('userId');
  const queryFirebaseToken = queryParams.get('refreshToken');

  const getUserRequest = useRef<TElkGetUserResponse>();
  const [user, setUser] = useState<UUser | undefined>({
    id: cookies.userId,
    authToken: cookies.authToken,
    name: '',
    phoneNumber: ''
  });
  const [userCredential, setUserCredential] = useState<UserCredential>();
  // const [email, setEmail] = useState<undefined | string>(undefined);
  const [accountExistsForPhone, setAccountExistsForPhone] = useState<boolean | undefined>();
  const [askForTextPermission, setAskForTextPermission] = useState(false);
  const [hasNudgedToAllowTexts, setHasNudgedToAllowTexts] = useState<boolean>(false);
  const [goToAfter, setGoToAfter] = useState<string | null>(null);
  const [navigateOptionsAfter, setNavigateOptionsAfter] = useState<NavigateOptions>();
  const [loginState, setLoginState] = useState<ULoginState>({
    status: (cookies.userId !== undefined && cookies.authToken !== undefined) ? ULoginStatus.LoggedIn : ULoginStatus.NotLoggedIn
  });
  const [savedUserInputs, setSavedUserInputs] = useState<UUserInputs>();
  const [eventImageHistory, setEventImageHistory] = useState<TElkGetImageHistoryResponse>();

  const userCreateActive = useRef(false);

  useEffect(() => {
    if (isLoggedIn() && goToAfter) {
      navigate(goToAfter, navigateOptionsAfter);
      setGoToAfter(null);
      setNavigateOptionsAfter(undefined);
    }
  }, [goToAfter, loginState.status]);

  useEffect(() => {
    if (queryUserId !== null && queryFirebaseToken !== null) {
      try {
        void loginFromQueryString(queryUserId, atob(queryFirebaseToken));
      } catch (e) {
        void logSumoEvent({
          app: ULogApplication.ELK,
          severity: ULogSeverity.INFO,
          userId: user?.id,
          tag: ULogTag.Network,
          message: `[UserContext] useEffect(queryFirebaseToken) - Error decoding firebase token "${queryFirebaseToken}": ${JSON.stringify(e)}`
        });

      }
    }
  }, [queryFirebaseToken]);

  useEffect(() => {
    if (loginState.status === ULoginStatus.WaitingForAccountCheck ||
      loginState.status === ULoginStatus.SignedInToFirebase) {
      if (accountExistsForPhone) {
        void continueLogin();
      } else {
        setLoginState({
          ...loginState,
          status: accountExistsForPhone === false ? ULoginStatus.AskingForName : ULoginStatus.WaitingForAccountCheck
        });
      }
    } else if (loginState.status === ULoginStatus.AfterLogin) {
      void doCreateElkUser();
    }
  }, [loginState.status, accountExistsForPhone]);

  const login = () => {
    navigate(loginUrl(location.pathname));
  };

  const isLoggedIn = () => {
    return loginState.status === ULoginStatus.LoggedIn;
  };

  const createUser = async (name: string, grantedTextPermissions: boolean, coinsSpent: number) => {
    setLoginState({
      ...loginState,
      status: ULoginStatus.NameSubmitting
    });

    setSavedUserInputs({
      name,
      grantedTextPermissions,
      coinsSpent
    });

    await continueLogin();

    setLoginState({
      ...loginState,
      status: ULoginStatus.AfterLogin
    });
  };

  const doCreateElkUser = async () => {
    if (!userCreateActive.current && savedUserInputs) {
      userCreateActive.current = true;
      try {
        await createElkUser(
          getAuthHeader(),
          savedUserInputs.name,
          user?.phoneNumber ?? '',
          savedUserInputs.grantedTextPermissions,
          savedUserInputs.coinsSpent
        );
        setLoginState({
          ...loginState,
          status: ULoginStatus.UserCreated
        });
        setSavedUserInputs(undefined);
      } catch (e) {
        void logSumoEvent({
          app: ULogApplication.ELK,
          severity: ULogSeverity.ERROR,
          tag: ULogTag.Network,
          message: `[UserContext] Error during createElkUser: ${JSON.stringify(e)}`
        });
        setLoginState({
          ...loginState,
          error: 'An error occurred trying to create your user, please try again. If this error persists, please contact us at support@sunshine.com.'
        });
      }
      userCreateActive.current = false;
    }
  }

  const submitPhone = async (simplePhone: string, force?: boolean) => {
    void checkForAccount(simplePhone);
    if (loginState.status !== ULoginStatus.PhoneSubmitting || force) {
      setLoginState({ ...loginState, status: ULoginStatus.PhoneSubmitting });
      try {
        const signInResponse = await sendPhoneSignInCode(TAppProduct.ELK, simplePhone);
        setLoginState({
          ...loginState,
          phone: simplePhone,
          status: ULoginStatus.CodeSent,
          signInResponse,
          codeResendTimestamp: Date.now(),
          error: undefined
        });

      } catch (e) {
        if (e instanceof FirebaseError && e.code === 'auth/invalid-phone-number') {
          setLoginState({
            ...loginState,
            status: ULoginStatus.NotLoggedIn,
            error: 'Invalid number, please try again.'
          });
        } else {
          void logSumoEvent({
            app: ULogApplication.ELK,
            severity: ULogSeverity.ERROR,
            userId: user?.id,
            tag: ULogTag.UserAuth,
            message: `Firebase login error: ${e} ${JSON.stringify(e)}`
          });

          setLoginState({
            ...loginState,
            status: ULoginStatus.NotLoggedIn,
            error: `Something went wrong during sign in: ${e}`
          });
        }
        return;
      }

      void logSumoEvent({
        app: ULogApplication.ELK,
        severity: ULogSeverity.INFO,
        userId: user?.id,
        tag: ULogTag.UserAuth,
        message: 'Firebase code send success'
      });
    }
  };

  const confirmCode = async (code: string) => {
    if (!loginState.signInResponse) {
      void logSumoEvent({
        app: ULogApplication.ELK,
        severity: ULogSeverity.INFO,
        userId: user?.id,
        tag: ULogTag.UserAuth,
        message: 'Firebase confirmationResult not set in onConfirmCode'
      });

      setLoginState({
        ...loginState,
        status: ULoginStatus.CodeSent,
        error: 'An error occurred trying to authenticate, please try again. If this error persists, please contact us at support@sunshine.com.'
      });
      return;
    }

    if (loginState.status === ULoginStatus.CodeSubmitting || !loginState.phone) {
      return;
    }

    try {
      const startTime = Date.now();

      setLoginState({
        ...loginState,
        status: ULoginStatus.CodeSubmitting
      });

      const signInResponse = await signInWithPhoneAndCode(TAppProduct.ELK, loginState.phone, code);

      if (!signInResponse.success) {
        if (signInResponse.errorMessage) {
          setLoginState({
            ...loginState,
            status: ULoginStatus.CodeSent,
            error: `Failed to verify phone code due to ${signInResponse.errorMessage}`
          });
        } else {
          setLoginState({
            ...loginState,
            status: ULoginStatus.CodeSent,
            error: 'Invalid code, please try again.'
          });
        }
        return;
      }

      if (signInResponse.firebaseCustomToken === undefined) {
        void logSumoEvent({
          app: ULogApplication.ELK,
          severity: ULogSeverity.WARN,
          userId: user?.id,
          tag: ULogTag.UserAuth,
          message: 'signInWithPhoneAndCode did not return a token'
        });

        setLoginState({
          ...loginState,
          status: ULoginStatus.CodeSent,
          error: 'An error occurred trying to authenticate, please try again. If this error persists, please contact us at support@sunshine.com.'
        });
        return;
      }

      await loginToFirebase(signInResponse.firebaseCustomToken, startTime);
    } catch (e) {
      if (e instanceof TimeoutError) {
        void logSumoEvent({
          app: ULogApplication.ELK,
          severity: ULogSeverity.ERROR,
          userId: user?.id,
          tag: ULogTag.UserAuth,
          message: 'Firebase code verification timeout'
        });
        setLoginState({
          ...loginState,
          status: ULoginStatus.CodeSent,
          error: 'Request timed out, try again later.'
        });
      } else {
        void logSumoEvent({
          app: ULogApplication.ELK,
          severity: ULogSeverity.ERROR,
          userId: user?.id,
          tag: ULogTag.UserAuth,
          message: `Firebase code verification error: ${e} ${JSON.stringify(e)}`
        });

        setLoginState({
          ...loginState,
          status: ULoginStatus.CodeSent,
          error: 'An error occurred trying to authenticate, please try again. If this error persists, please contact us at support@sunshine.com.'
        });
      }
    }
  };

  const loginFromQueryString = async (userId: string, token: string) => {
    try {
      setAccountExistsForPhone(true);
      const response = await refetchAccessToken(userId, deviceId, token);
      if (response.customFirebaseToken) {
        await loginToFirebase(response.customFirebaseToken);
      } else {
        setLoginState({
          ...loginState,
          status: ULoginStatus.ErrorDuringLogin,
          error: 'Missing custom firebase token'
        });
      }
    } catch (e) {
      void logSumoEvent({
        app: ULogApplication.ELK,
        severity: ULogSeverity.WARN,
        userId: user?.id,
        tag: ULogTag.Network,
        message: `[UserContext] loginFromQueryString - Error: ${JSON.stringify(e)}`
      });

      setLoginState({
        ...loginState,
        status: ULoginStatus.ErrorDuringLogin,
        error: e?.message ?? e?.name ?? e?.reason ?? e.toString()
      });
    }
  }

  const loginToFirebase = async (firebaseCustomToken: string, startTime?: number) => {
    const result: UserCredential = await promiseTimeout(
      signInWithCustomToken(auth, firebaseCustomToken),
      10000
    );

    if (startTime !== undefined) {
      void logSumoEvent({
        app: ULogApplication.ELK,
        severity: ULogSeverity.INFO,
        userId: user?.id,
        tag: ULogTag.Network,
        message: `FirebaseCodeConfirmEnd - elapsed=${Date.now() - startTime}, reqId=${getCurrentRequestId()}, FirebaseUser=${result.user.uid}`
      });
    }

    // Save for later -- to possibly be used in createUser after user has supplied their name and we create the ElkUser
    setUserCredential(result);

    setLoginState({
      ...loginState,
      status: ULoginStatus.SignedInToFirebase
    });
  };

  const restartLogin = () => {
    setLoginState({
      ...loginState,
      status: (cookies.userId !== undefined && cookies.authToken !== undefined) ? ULoginStatus.LoggedIn : ULoginStatus.NotLoggedIn,
      error: undefined
    });
  };

  const handleLogin = async (firebaseUser: User, goTo?: string) => {
    if (firebaseUser.phoneNumber === null) {
      throw new Error('Phone number is null');
    }

    if (goTo) {
      setGoToAfter(goTo);
    }

    try {
      const authenticateResponse = await authenticate({
          deviceId,
          verifiedPhoneNumber: firebaseUser.phoneNumber,
          firebaseIDToken: await firebaseUser.getIdToken(),
          product: TAppProduct.ELK
        }
      );

      setUser({
        id: authenticateResponse.userId,
        authToken: authenticateResponse.accessToken,
        name: user?.name ?? '',
        phoneNumber: firebaseUser.phoneNumber ?? '',
        grantedTextPermission: user?.grantedTextPermission,
        emails: user?.emails
      });
      setLoginState({
        ...loginState,
        status: ULoginStatus.AfterLogin
      });

      setCookie('authToken', authenticateResponse.accessToken, {
        path: '/',
        maxAge: sixtyDays
      });

      setCookie('userId', authenticateResponse.userId, {
        path: '/',
        maxAge: sixtyDays
      });
    } catch (e) {
      void logSumoEvent({
        app: ULogApplication.ELK,
        severity: ULogSeverity.ERROR,
        userId: user?.id,
        tag: ULogTag.UserAuth,
        message: `Error during Sunshine authenticate and login: ${JSON.stringify(e)}`
      });
    }
  };

  const continueLogin = async () => {
    if (userCredential) {
      await handleLogin(userCredential.user);
    }
  }

  const finishLogin = (goTo?: string, options?: NavigateOptions) => {
    if (loginState.status === ULoginStatus.AfterLogin || loginState.status === ULoginStatus.UserCreated) {
      setLoginState({
        ...loginState,
        status: ULoginStatus.LoggedIn
      });
      setGoToAfter(goTo ?? null);
      setNavigateOptionsAfter(options);
    } else if (goTo) {
      navigate(goTo, options);
    }
  };

  const getAuthHeader = (isBlocking?: boolean): TAuthHeader => {
    if (user?.id !== undefined && user?.authToken !== undefined) {
      return new TAuthHeader({
        userId: user.id,
        accessToken: user.authToken,
        timestamp: Math.floor(new Date().getTime() / 1000),
        deviceId,
        agent: new TUserAgent({
          platform: 'web',
          version: '0.0.0.2',
          // deviceVersion: detectBrowser(),
          // osVersion: detectOS(),
          product: TAppProduct.ELK
        }),
        requestId: `${isBlocking === false ? 'nonblocking-' : 'blocking-'}${getCurrentRequestId()}`
      });
    }

    void logSumoEvent({
      app: ULogApplication.ELK,
      severity: ULogSeverity.ERROR,
      userId: user?.id,
      tag: ULogTag.UserAuth,
      message: `AuthHeader not available if user is not logged in: ${new Error().stack}`
    });

    throw new Error('AuthHeader not available if user is not logged in.');
  };

  const setName = (namehere: string) => {
    if (user) {
      setUser({ ...user, name: namehere });
    }
  };

  const setEmail = (email: string) => {
    if (user) {
      user.emails = [email, ...(user.emails ?? [])];
    }
  };

  const getEmail = (): string | undefined => {
    if ((user?.emails?.length ?? 0) > 0) {
      return user?.emails?.[0];
    }
    return undefined;
  }

  const setGrantedTextPermissions = (permissionGranted: boolean) => {
    if (user) {
      setUser({ ...user, grantedTextPermission: permissionGranted });
    }
  };

  const setPhoneNumber = (phone: string) => {
    if (user) {
      setUser({ ...user, phoneNumber: phone });
    }
  };

  const setPhotoUrl = (photoUrl?: string) => {
    if (user) {
      setUser({ ...user, photoUrl: photoUrl });
    }
  };

  const fetchAndSaveUser = async (): Promise<UUser> => {
    if (getUserRequest.current === undefined) {
      getUserRequest.current = await getElkUser(getAuthHeader());
    }
    const elkUser = await getUserRequest.current;

    const photoUrl = elkUser.elkUser.photoUrl !== undefined ? await getNormalizedPhotoUrl(
      elkUser.elkUser.photoUrl) : undefined;
    const newUser: UUser = {
      id: elkUser.elkUser.userId,
      authToken: cookies.authToken ?? '',
      name: elkUser.elkUser.name ?? '',
      phoneNumber: elkUser.elkUser?.phoneNumber ?? '',
      photoUrl,
      grantedTextPermission: elkUser.elkUser?.grantedTextPermission,
      emails: elkUser?.elkUser.emails
    };

    getUserRequest.current = undefined;

    setAskForTextPermission(elkUser.elkUser?.textPermissionStatus === TAppElkTextPermissionStatus.UNDEFINED);
    setUser(newUser);

    return newUser;
  };

  const getUserName = async (): Promise<string | null> => {
    if (user?.name) {
      return user.name;
    }

    const newUser = await fetchAndSaveUser();
    return newUser.name;
  };

  const getPhoneNumber = async (): Promise<string | null> => {
    if (user?.phoneNumber) {
      return user.phoneNumber;
    }

    const newUser = await fetchAndSaveUser();
    return newUser.phoneNumber;
  };

  const checkForAccount = async (phoneNumber: string): Promise<void> => {
    setAccountExistsForPhone(undefined);
    if (user) {
      const res = await getHasCompletedElkProfile(phoneNumber, false);
      setAccountExistsForPhone(res.existingElkProfile);
      // setAskForTextPermission(res.askTextPermission ?? false);
    }
  };

  const generateElkUser = (partialUser?: Partial<TAppElkUser>): TAppElkUser | undefined => {
    if (user) {
      if (partialUser) {
        user.id = user.id ?? '';
        user.name = partialUser.name ?? user.name;
        user.photoUrl = partialUser.photoUrl ?? user.photoUrl;
        user.grantedTextPermission = partialUser.grantedTextPermission ?? user.grantedTextPermission;
        user.emails = partialUser?.emails ?? user?.emails;
        setUser({ ...user });
        setLoginState({
          status: ULoginStatus.LoggedIn
        });
      }
      return new TAppElkUser({
        userId: user.id,
        name: partialUser?.name ?? user?.name ?? '',
        phoneNumber: user?.phoneNumber ?? '',
        photoUrl: partialUser?.photoUrl ?? user.photoUrl,
        grantedTextPermission: partialUser?.grantedTextPermission ?? user.grantedTextPermission ?? false,
        textPermissionStatus: partialUser?.textPermissionStatus ?? TAppElkTextPermissionStatus.UNDEFINED,
        emails: partialUser?.emails ?? user?.emails ?? undefined
      });
    }
    return undefined;
  };

  const getDeviceId = () => {
    let thisDeviceId = deviceId;
    if (deviceId === '') {
      thisDeviceId = `WEB-${uuidv4()}`;
      console.log('[UserContext] Setting device id');
      setDeviceId(thisDeviceId);
    }
    return thisDeviceId;
  };

  const fetchEventImageHistory = async (): Promise<TElkGetImageHistoryResponse> => {
    if (eventImageHistory !== undefined) {
      return eventImageHistory;
    }

    const history = await getEventImageHistory(context);

    setEventImageHistory(history);
    return history;
  };

  const logout = async () => {
    deleteCookie('authToken', { path: '/' });
    deleteCookie('userId', { path: '/' });

    void logSumoEvent({
      app: ULogApplication.ELK,
      severity: ULogSeverity.INFO,
      userId: user?.id,
      tag: ULogTag.UserAuth,
      message: 'User logout'
    });

    try {
      await getAuth(app).signOut();
    } catch (e) {
      void logSumoEvent({
        app: ULogApplication.ELK,
        severity: ULogSeverity.ERROR,
        userId: user?.id,
        tag: ULogTag.UserAuth,
        message: `Error during Firebase signOut: ${JSON.stringify(e)}`
      });
    }

    navigate('/');
    window.location.reload();
  };

  const context: IUUserContext = {
    login,
    loginState,
    id: user?.id,
    token: user?.authToken,
    phoneNumber: user?.phoneNumber ?? '',
    name: user?.name ?? '',
    emails: user?.emails,
    photoUrl: user?.photoUrl,
    askForTextPermission,
    setAskForTextPermission,
    grantedTextPermission: user?.grantedTextPermission,
    hasNudgedToAllowTexts: hasNudgedToAllowTexts,
    accountExistsForPhone,
    checkForAccount,
    isLoggedIn,
    confirmCode,
    restartLogin,
    handleLogin,
    continueLogin,
    finishLogin,
    submitPhone,
    getAuthHeader,
    getPhoneNumber,
    logout,
    getUserName,
    setName,
    setPhoneNumber,
    setPhotoUrl,
    createUser,
    generateElkUser,
    setHasNudgedToAllowTexts,
    setGrantedTextPermissions,
    setEmail,
    getEmail,
    deviceId: getDeviceId(),
    fetchEventImageHistory
  };

  return (
    <UserContext.Provider value={context}>
      {props.children}
    </UserContext.Provider>
  );
});

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (context === null) {
    throw new Error('useUserContext must be used within a UserContextProvider');
  }
  return context;
};
